import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { GelanggangDetail, UserProfile, NoticeItem, GelanggangClassItem } from '../../types';
import { useToast } from './Toast';
import { saveGelanggangDB } from '../../services/db';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import { getGoogleMapsUrl, getGelanggangDisplayAddress, formatPublicName, isUserAbove18, canEditGelanggangSessionAndPrice } from '../../utils/formatters';
import { parseGelanggangClasses, getYearlyPrice, isEligibleForMonthlyFee } from '../../utils/pricing';
import { parseStartAndEndTime } from '../../utils/activityScheduler';
import { BengkungCulaBadge } from './BengkungCulaBadge';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Navigation,
  Phone,
  User,
  Users,
  Save,
  Plus,
  Trash2,
  CheckCircle2,
  DollarSign,
  Target,
  HandFist,
  AlertCircle,
  ExternalLink,
  Info,
  Sparkles,
  Settings,
  ChevronDown,
  ChevronUp,
  Search,
  MessageCircle,
  ShieldAlert,
  Loader2,
  Sun,
  Moon,
  Timer,
  QrCode,
  CreditCard,
  Upload,
  Image as ImageIcon,
  FileText,
  Check,
  Copy,
  Building2,
  Wallet
} from 'lucide-react';

interface GelanggangSessionManageViewProps {
  gelanggang: GelanggangDetail;
  onBack: () => void;
  onSaved: (updatedGel: GelanggangDetail) => void;
  onOpenCalendar?: (g: GelanggangDetail) => void;
  membersList?: UserProfile[];
  notices?: NoticeItem[];
}

const DAYS_OF_WEEK = [
  { key: 'ahad', label: 'Ahad', full: 'Ahad' },
  { key: 'isnin', label: 'Isnin', full: 'Isnin' },
  { key: 'selasa', label: 'Selasa', full: 'Selasa' },
  { key: 'rabu', label: 'Rabu', full: 'Rabu' },
  { key: 'khamis', label: 'Khamis', full: 'Khamis' },
  { key: 'jumaat', label: 'Jumaat', full: 'Jumaat' },
  { key: 'sabtu', label: 'Sabtu', full: 'Sabtu' },
];

function time24To12(time24: string): string {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 === 0 ? 12 : h % 12;
  const mFormatted = m < 10 ? `0${m}` : `${m}`;
  return `${h}.${mFormatted} ${period}`;
}

function time12To24(time12: string): string {
  if (!time12) return '20:30';
  const match = time12.match(/(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?/i);
  if (!match) return '20:30';
  let h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  const period = match[3] ? match[3].toUpperCase() : '';
  if ((period === 'PM' || /malam|petang/i.test(period)) && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  const hStr = h < 10 ? `0${h}` : `${h}`;
  const mStr = m < 10 ? `0${m}` : `${m}`;
  return `${hStr}:${mStr}`;
}

function calculateDuration(start24: string, end24: string): string {
  if (!start24 || !end24) return '';
  const [h1, m1] = start24.split(':').map(Number);
  const [h2, m2] = end24.split(':').map(Number);
  let totalMin1 = h1 * 60 + m1;
  let totalMin2 = h2 * 60 + m2;
  if (totalMin2 < totalMin1) {
    totalMin2 += 24 * 60; // overnight session
  }
  const diff = totalMin2 - totalMin1;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  if (hours === 0 && mins === 0) return '0 Jam';
  if (mins === 0) return `${hours} Jam`;
  return `${hours} Jam ${mins} Minit`;
}

function parseDaysFromSessionTime(str: string): string[] {
  if (!str) return ['jumaat', 'sabtu'];
  const lower = str.toLowerCase();
  const matched = DAYS_OF_WEEK.filter(d => lower.includes(d.key)).map(d => d.key);
  return matched.length > 0 ? matched : ['jumaat', 'sabtu'];
}

function parseDaysFromDayString(dayStr?: string): string[] {
  if (!dayStr) return [];
  const lower = dayStr.toLowerCase();
  return DAYS_OF_WEEK.filter(d => lower.includes(d.key)).map(d => d.key);
}

function formatDaysFromKeys(keys: string[]): string {
  if (keys.length === 0) return '';
  const ordered = DAYS_OF_WEEK.filter(d => keys.includes(d.key));
  if (ordered.length === 1) return ordered[0].label;
  if (ordered.length === 2) return `${ordered[0].label} & ${ordered[1].label}`;
  const last = ordered[ordered.length - 1].label;
  const others = ordered.slice(0, ordered.length - 1).map(d => d.label).join(', ');
  return `${others} & ${last}`;
}

export const GelanggangSessionManageView: React.FC<GelanggangSessionManageViewProps> = ({
  gelanggang,
  onBack,
  onSaved,
  onOpenCalendar,
  membersList = [],
}) => {
  const { currentUser } = useAuth();
  const canEdit = canEditGelanggangSessionAndPrice(currentUser, gelanggang);
  const { showToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Gelanggang basic details state
  const [name] = useState(gelanggang.name);
  const [status, setStatus] = useState<'Aktif' | 'Tidak Aktif'>(gelanggang.status || 'Aktif');
  const [location, setLocation] = useState(gelanggang.location || '');
  const [address, setAddress] = useState(gelanggang.address || gelanggang.location || '');
  const [gpsLocation, setGpsLocation] = useState(gelanggang.gpsLocation || '');

  // Dynamic Ketua Gelanggang resolution from member roles
  const dynamicLeader = useMemo(() => {
    const assigned = membersList.find(
      m => m.role === 'Ketua Gelanggang' && (m.gelanggang === gelanggang.name || (m as any).gelanggangId === gelanggang.id)
    ) || membersList.find(
      m => m.name && gelanggang.leaderName && m.name.trim().toLowerCase() === gelanggang.leaderName.trim().toLowerCase()
    );

    if (assigned) {
      return {
        name: assigned.name,
        phone: assigned.phone || gelanggang.leaderPhone || '',
        memberId: assigned.id,
        bengkung: assigned.bengkung || ''
      };
    }

    return {
      name: gelanggang.leaderName || '',
      phone: gelanggang.leaderPhone || '',
      memberId: '',
      bengkung: ''
    };
  }, [membersList, gelanggang]);

  // Payment preference state (FPX Persatuan vs Own QR Ketua Gelanggang)
  const [paymentPreference, setPaymentPreference] = useState<'fpx' | 'qr_ketua'>(gelanggang.paymentPreference || 'fpx');
  const [qrBankName, setQrBankName] = useState<string>(gelanggang.qrBankName || 'Maybank');
  const [qrAccountName, setQrAccountName] = useState<string>(gelanggang.qrAccountName || gelanggang.leaderName || '');
  const [qrAccountNo, setQrAccountNo] = useState<string>(gelanggang.qrAccountNo || '');
  const [qrImageUrl, setQrImageUrl] = useState<string>(gelanggang.qrImageUrl || '');
  const [qrNotes, setQrNotes] = useState<string>(gelanggang.qrNotes || '');
  const [isQrUploading, setIsQrUploading] = useState<boolean>(false);

  useEffect(() => {
    if (!qrAccountName && dynamicLeader.name) {
      setQrAccountName(dynamicLeader.name);
    }
  }, [dynamicLeader.name]);

  const handleQrImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila muat naik fail imej (PNG / JPEG / WebP) bagi kod QR.');
      return;
    }
    setIsQrUploading(true);
    try {
      // Auto-process and decode QR code into clean 1:1 SVG
      const processed = await processUploadedQrToSvg(file);
      if (processed) {
        setQrImageUrl(processed.svgDataUrl);
        setIsQrUploading(false);
        showToast('success', 'Kod QR Ditukar ke Digital SVG (1:1)', 'Imej kod QR berjaya diimbas dan dijana semula ke format 1:1 SVG digital tulen tanpa sebarang bingkai.');
        return;
      }
    } catch (err) {
      console.warn('Auto SVG QR processing error:', err);
    }

    // Fallback if jsQR cannot detect QR pattern in file
    const reader = new FileReader();
    reader.onload = () => {
      setQrImageUrl(reader.result as string);
      setIsQrUploading(false);
      showToast('info', 'Imej Dimuat Naik', 'Imej disimpan. (Nota: Tiada corak QR dikesan untuk penjanaan SVG, imej asal digunakan).');
    };
    reader.onerror = () => {
      setIsQrUploading(false);
      showToast('error', 'Ralat', 'Gagal membaca imej kod QR.');
    };
    reader.readAsDataURL(file);
  };

  // Schedule builder state
  const [selectedDays, setSelectedDays] = useState<string[]>(() => parseDaysFromSessionTime(gelanggang.sessionTime));

  // Parse initial start and end time from gelanggang sessionTime
  const initialTimeParsed = useMemo(() => {
    const { startHour, startMinute, endHour, endMinute } = parseStartAndEndTime(gelanggang.sessionTime);
    const sH = startHour < 10 ? `0${startHour}` : `${startHour}`;
    const sM = startMinute < 10 ? `0${startMinute}` : `${startMinute}`;
    const eH = endHour < 10 ? `0${endHour}` : `${endHour}`;
    const eM = endMinute < 10 ? `0${endMinute}` : `${endMinute}`;
    return {
      start: `${sH}:${sM}`,
      end: `${eH}:${eM}`
    };
  }, [gelanggang.sessionTime]);

  const [globalStartTime, setGlobalStartTime] = useState<string>(initialTimeParsed.start);
  const [globalEndTime, setGlobalEndTime] = useState<string>(initialTimeParsed.end);

  // Toggle for separate start/end time per day
  const [isDifferentTimePerDay, setIsDifferentTimePerDay] = useState<boolean>(false);
  const [dayTimes, setDayTimes] = useState<Record<string, { start: string; end: string }>>(() => {
    const init: Record<string, { start: string; end: string }> = {};
    DAYS_OF_WEEK.forEach(d => {
      init[d.key] = { start: initialTimeParsed.start, end: initialTimeParsed.end };
    });
    return init;
  });

  // Manual schedule string mode
  const [isManualScheduleMode, setIsManualScheduleMode] = useState<boolean>(false);
  const [manualSessionTime, setManualSessionTime] = useState<string>(gelanggang.sessionTime || '');

  // Classes state
  const [classesList, setClassesList] = useState<GelanggangClassItem[]>(() => {
    return parseGelanggangClasses(gelanggang.name, gelanggang.classes).map(c => {
      const clsAny = c as any;
      let sTime = clsAny.startTime;
      let eTime = clsAny.endTime;
      let dDay = clsAny.day || '';

      // If class already has schedule string, extract start/end time and day if not explicitly set
      if ((!sTime || !eTime) && clsAny.schedule) {
        const parsed = parseStartAndEndTime(clsAny.schedule);
        if (parsed) {
          const sH = parsed.startHour < 10 ? `0${parsed.startHour}` : `${parsed.startHour}`;
          const sM = parsed.startMinute < 10 ? `0${parsed.startMinute}` : `${parsed.startMinute}`;
          const eH = parsed.endHour < 10 ? `0${parsed.endHour}` : `${parsed.endHour}`;
          const eM = parsed.endMinute < 10 ? `0${parsed.endMinute}` : `${parsed.endMinute}`;
          sTime = `${sH}:${sM}`;
          eTime = `${eH}:${eM}`;
        }
      }
      if (!dDay && clsAny.schedule) {
        const dayMatch = DAYS_OF_WEEK.filter(d => clsAny.schedule.toLowerCase().includes(d.key)).map(d => d.label);
        if (dayMatch.length === 1) dDay = dayMatch[0];
        else if (dayMatch.length === 2) dDay = `${dayMatch[0]} & ${dayMatch[1]}`;
      }

      // Sensible defaults for Memanah (traditional archery usually runs Sunday mornings)
      if (!sTime && c.name.toLowerCase().includes('memanah')) {
        sTime = '08:00';
      }
      if (!eTime && c.name.toLowerCase().includes('memanah')) {
        eTime = '10:00';
      }
      if (!dDay && c.name.toLowerCase().includes('memanah')) {
        dDay = 'Ahad';
      }

      const finalSTime = sTime || initialTimeParsed.start;
      const finalETime = eTime || initialTimeParsed.end;
      const cleanDay = (dDay && dDay !== 'Semua Hari Sesi' && dDay !== 'Ikut Sesi Gelanggang') ? dDay : '';
      const timeRange = `${time24To12(finalSTime)} - ${time24To12(finalETime)}`;
      const formattedSched = cleanDay ? `${cleanDay} (${timeRange})` : timeRange;

      return {
        name: c.name,
        price: c.price,
        day: cleanDay || undefined,
        startTime: finalSTime,
        endTime: finalETime,
        schedule: clsAny.schedule || formattedSched,
        category: clsAny.category || (c.name.toLowerCase().includes('dewasa') ? 'Dewasa' : c.name.toLowerCase().includes('muda') ? 'Kanak-kanak' : 'Terbuka'),
        notes: clsAny.notes || ''
      };
    });
  });

  // Helper to update a class item immutably with auto-calculated schedule string
  const updateClassItem = (index: number, updates: Partial<GelanggangClassItem>) => {
    setClassesList(prev => {
      const next = [...prev];
      const current = next[index];
      const sTime = updates.startTime !== undefined ? updates.startTime : (current.startTime || globalStartTime);
      const eTime = updates.endTime !== undefined ? updates.endTime : (current.endTime || globalEndTime);
      const dayVal = updates.day !== undefined ? updates.day : current.day;
      const cleanDay = (dayVal && dayVal !== 'Semua Hari Sesi' && dayVal !== 'Ikut Sesi Gelanggang') ? dayVal : '';
      const timeRange = `${time24To12(sTime)} - ${time24To12(eTime)}`;
      const schedule = cleanDay ? `${cleanDay} (${timeRange})` : timeRange;

      next[index] = {
        ...current,
        ...updates,
        startTime: sTime,
        endTime: eTime,
        day: cleanDay || undefined,
        schedule
      };
      return next;
    });
  };

  // Add new class form state
  const [newClassName, setNewClassName] = useState<string>('Silat');
  const [customClassName, setCustomClassName] = useState<string>('');
  const [newClassPrice, setNewClassPrice] = useState<number>(40);
  const [newClassDay, setNewClassDay] = useState<string>('Semua Hari Sesi');
  const [newClassStartTime, setNewClassStartTime] = useState<string>('20:30');
  const [newClassEndTime, setNewClassEndTime] = useState<string>('22:30');
  const [newClassCategory, setNewClassCategory] = useState<'Dewasa' | 'Kanak-kanak' | 'Terbuka'>('Terbuka');

  // Filter registered pesilat for this gelanggang
  const enrolledMembers = useMemo(() => {
    return membersList.filter(m => {
      if (m.status && !['Aktif', 'Menunggu Pengesahan'].includes(m.status)) return false;
      if (m.gelanggang !== gelanggang.name) return false;
      if (m.role === 'Waris') return false;
      if (m.role === 'Ketua Gelanggang') return false;
      if (dynamicLeader.name && m.name && (
        m.name.trim().toLowerCase() === dynamicLeader.name.trim().toLowerCase() ||
        m.name.toLowerCase().includes(dynamicLeader.name.toLowerCase()) ||
        dynamicLeader.name.toLowerCase().includes(m.name.toLowerCase())
      )) return false;
      return true;
    });
  }, [membersList, gelanggang.name, dynamicLeader.name]);

  // Dynamic schedule string generation
  const generatedScheduleString = useMemo(() => {
    if (isManualScheduleMode) {
      return manualSessionTime;
    }
    if (selectedDays.length === 0) {
      const gRange = `${time24To12(globalStartTime)} - ${time24To12(globalEndTime)}`;
      return `(${gRange})`;
    }

    const orderedDays = DAYS_OF_WEEK.filter(d => selectedDays.includes(d.key));

    // If separate times per day
    if (isDifferentTimePerDay) {
      return orderedDays.map(d => {
        const t = dayTimes[d.key] || { start: globalStartTime, end: globalEndTime };
        const range = `${time24To12(t.start)} - ${time24To12(t.end)}`;
        return `${d.label} (${range})`;
      }).join(' & ');
    }

    // Shared time for all selected days
    let daysStr = '';
    if (orderedDays.length === 1) {
      daysStr = orderedDays[0].label;
    } else if (orderedDays.length === 2) {
      daysStr = `${orderedDays[0].label} & ${orderedDays[1].label}`;
    } else {
      const last = orderedDays[orderedDays.length - 1].label;
      const others = orderedDays.slice(0, orderedDays.length - 1).map(d => d.label).join(', ');
      daysStr = `${others} & ${last}`;
    }

    const timeRangeStr = `${time24To12(globalStartTime)} - ${time24To12(globalEndTime)}`;
    return `${daysStr} (${timeRangeStr})`;
  }, [isManualScheduleMode, manualSessionTime, selectedDays, isDifferentTimePerDay, dayTimes, globalStartTime, globalEndTime]);

  // Projected monthly & yearly revenue
  const financialSummary = useMemo(() => {
    let monthlyTotal = 0;
    enrolledMembers.forEach(mem => {
      if (isEligibleForMonthlyFee(mem)) {
        const isDewasa = mem.relationship !== 'Anak' && (mem.icNo ? isUserAbove18(mem.icNo) : true);
        const matched = classesList.find(c => {
          if (c.name.toLowerCase().includes('dewasa') && isDewasa) return true;
          if (c.name.toLowerCase().includes('muda') && !isDewasa) return true;
          return c.name.toLowerCase().includes('silat');
        }) || classesList[0];
        if (matched) {
          monthlyTotal += matched.price;
        }
      }
    });
    const yearlyTotal = getYearlyPrice(monthlyTotal);
    return { monthlyTotal, yearlyTotal };
  }, [enrolledMembers, classesList]);

  // Day toggle handler
  const handleToggleDay = (dayKey: string) => {
    if (!canEdit) return;
    if (selectedDays.includes(dayKey)) {
      setSelectedDays(selectedDays.filter(k => k !== dayKey));
    } else {
      setSelectedDays([...selectedDays, dayKey]);
    }
  };

  // Add class handler
  const handleAddClass = () => {
    if (!canEdit) return;
    const finalName = newClassName === 'Lain-lain' ? customClassName.trim() : newClassName;
    if (!finalName) {
      showToast('error', 'Nama Kelas Diperlukan', 'Sila masukkan nama aktiviti atau kelas latihan.');
      return;
    }
    if (classesList.some(c => c.name.toLowerCase() === finalName.toLowerCase())) {
      showToast('error', 'Kelas Telah Wujud', `Kelas ${finalName} sudah berada dalam senarai.`);
      return;
    }

    const timeRange = `${time24To12(newClassStartTime)} - ${time24To12(newClassEndTime)}`;
    const scheduleStr = newClassDay && newClassDay !== 'Semua Hari Sesi'
      ? `${newClassDay} (${timeRange})`
      : timeRange;

    const newClass: GelanggangClassItem = {
      name: finalName,
      price: Math.max(0, newClassPrice),
      day: newClassDay !== 'Semua Hari Sesi' ? newClassDay : undefined,
      startTime: newClassStartTime,
      endTime: newClassEndTime,
      schedule: scheduleStr,
      category: newClassCategory,
    };

    setClassesList([...classesList, newClass]);
    if (newClassName === 'Lain-lain') setCustomClassName('');
    showToast('success', 'Kelas Ditambah', `Kelas ${finalName} telah ditambah dengan waktu ${timeRange}.`);
  };

  // Remove class handler
  const handleRemoveClass = (index: number) => {
    if (!canEdit) return;
    const target = classesList[index];
    setClassesList(classesList.filter((_, idx) => idx !== index));
    showToast('info', 'Kelas Dikeluarkan', `Kelas ${target.name} telah dikeluarkan.`);
  };

  // List of classes that have custom start/end time or custom training day
  const classesWithCustomTimes = useMemo(() => {
    return classesList.filter(c => {
      const isDiffStart = c.startTime && c.startTime !== globalStartTime;
      const isDiffEnd = c.endTime && c.endTime !== globalEndTime;
      const isDiffDay = !!c.day && c.day !== 'Semua Hari Sesi' && c.day !== 'Ikut Sesi Gelanggang';
      return isDiffStart || isDiffEnd || isDiffDay;
    });
  }, [classesList, globalStartTime, globalEndTime]);

  // Save all changes
  const handleSaveAll = async () => {
    if (!canEdit) {
      showToast('error', 'Akses Terhad', 'Hanya Ketua Gelanggang bagi gelanggang ini atau Superadmin dibenarkan menyimpan perubahan.');
      return;
    }
    setIsSaving(true);
    try {
      const finalSessionTime = isManualScheduleMode ? manualSessionTime.trim() : generatedScheduleString.trim();

      // Ensure each class has its updated schedule string and times
      const finalizedClasses = classesList.map(c => {
        const sTime = c.startTime || globalStartTime;
        const eTime = c.endTime || globalEndTime;
        const cleanDay = (c.day && c.day !== 'Semua Hari Sesi' && c.day !== 'Ikut Sesi Gelanggang') ? c.day : '';
        const timeRange = `${time24To12(sTime)} - ${time24To12(eTime)}`;
        const sched = cleanDay ? `${cleanDay} (${timeRange})` : timeRange;
        return {
          ...c,
          startTime: sTime,
          endTime: eTime,
          day: cleanDay || undefined,
          schedule: sched
        };
      });

      const updatedGelanggang: GelanggangDetail = {
        ...gelanggang,
        name: gelanggang.name,
        status,
        sessionTime: finalSessionTime || gelanggang.sessionTime,
        location: location.trim() || gelanggang.location,
        address: address.trim() || gelanggang.address,
        gpsLocation: gpsLocation.trim(),
        leaderName: dynamicLeader.name || gelanggang.leaderName,
        leaderPhone: dynamicLeader.phone || gelanggang.leaderPhone,
        classes: JSON.stringify(finalizedClasses),
        paymentPreference,
        qrBankName: qrBankName.trim(),
        qrAccountName: qrAccountName.trim(),
        qrAccountNo: qrAccountNo.trim(),
        qrImageUrl,
        qrNotes: qrNotes.trim()
      };

      await saveGelanggangDB(updatedGelanggang);
      showToast('success', 'Perubahan Disimpan', `Jadual sesi latihan, waktu mula/tamat, dan harga gelanggang ${gelanggang.name} berjaya dikemaskini.`);
      onSaved(updatedGelanggang);
    } catch (err) {
      showToast('error', 'Gagal Menyimpan', 'Berlaku ralat semasa menyimpan maklumat. Sila cuba lagi.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-16 animate-fade-in">
      {/* Top Header & Sticky Action Bar */}
      <div className="bg-white p-4 sm:p-6 rounded-3xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border-none cursor-pointer shrink-0 mt-0.5"
            title="Kembali ke Senarai Gelanggang"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-red-50 text-asmm-red text-[11px] font-extrabold rounded-full border border-red-100">
                <Settings className="w-3.5 h-3.5" />
                <span>Pengurusan Sesi & Harga</span>
              </span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[11px] font-extrabold rounded-full border ${status === 'Aktif'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status === 'Aktif' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                <span>{status}</span>
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              Gelanggang {gelanggang.name}
            </h1>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate max-w-md">{address || location || 'Kedah Darul Aman'}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 self-end md:self-center">
          {onOpenCalendar && (
            <button
              type="button"
              onClick={() => onOpenCalendar(gelanggang)}
              className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-2xl transition-all border-none cursor-pointer flex items-center gap-2"
            >
              <Calendar className="w-4 h-4 text-slate-600" />
              <span className="hidden sm:inline">Kalendar Latihan</span>
            </button>
          )}

          {canEdit ? (
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson disabled:opacity-50 text-white text-xs font-black rounded-2xl transition-all shadow-md hover:shadow-lg flex items-center gap-2 cursor-pointer border-none"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Simpan Perubahan</span>
                </>
              )}
            </button>
          ) : (
            <div className="px-3.5 py-2 bg-slate-100 text-slate-600 text-xs font-bold rounded-2xl flex items-center gap-1.5 border border-slate-200">
              <ShieldAlert className="w-4 h-4 text-slate-500" />
              <span>Mod Paparan Sahaja</span>
            </div>
          )}
        </div>
      </div>

      {/* Read-Only Warning Banner */}
      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 p-4 sm:p-5 rounded-3xl flex items-start gap-3.5 text-xs shadow-xs">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="font-extrabold text-amber-950 text-sm flex items-center gap-2">
              <span>Mod Paparan Sahaja (Akses Terhad)</span>
              <span className="text-[10px] bg-amber-200/80 text-amber-900 font-black px-2 py-0.5 rounded-full">Hanya Ketua Gelanggang / Superadmin</span>
            </h4>
            <p className="text-amber-800 leading-relaxed text-xs">
              Hanya Ketua Gelanggang bagi <strong>Gelanggang {gelanggang.name}</strong> ({dynamicLeader.name || gelanggang.leaderName || 'Ketua Gelanggang'}) atau <strong>Superadmin</strong> yang dibenarkan mengubah jadual sesi latihan, masa, senarai kelas, saluran bayaran, dan harga yuran gelanggang ini.
            </p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Columns: Schedule Builder & Classes Management */}
        <div className="lg:col-span-2 space-y-6">

          {/* Card 1: Jadual & Waktu Latihan Utama (Explicit Start Time & End Time) */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
                  <Clock className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-sm font-black text-slate-900">Jadual & Masa Sesi Latihan (Start Time - End Time)</h2>
                  <p className="text-[11px] text-slate-500">Tetapkan hari latihan mingguan berserta masa mula dan masa tamat setiap sesi.</p>
                </div>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    if (!isManualScheduleMode) {
                      setManualSessionTime(generatedScheduleString);
                    }
                    setIsManualScheduleMode(!isManualScheduleMode);
                  }}
                  className="text-[11px] font-bold text-slate-500 hover:text-slate-800 underline bg-transparent border-none cursor-pointer self-start sm:self-center"
                >
                  {isManualScheduleMode ? 'Gunakan Pemilih Masa Mula & Tamat' : 'Mod Teks Manual'}
                </button>
              )}
            </div>

            {!isManualScheduleMode ? (
              <div className="space-y-5">
                {/* Day of Week Chips */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-2">Pilih Hari Latihan Mingguan</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                    {DAYS_OF_WEEK.map(day => {
                      const active = selectedDays.includes(day.key);
                      return (
                        <button
                          key={day.key}
                          type="button"
                          disabled={!canEdit}
                          onClick={() => handleToggleDay(day.key)}
                          className={`py-2.5 px-2 rounded-xl text-xs font-black transition-all border text-center ${!canEdit ? 'cursor-default opacity-85' : 'cursor-pointer'
                            } ${active
                              ? 'bg-slate-900 text-amber-400 border-slate-900 shadow-xs ring-2 ring-slate-900/20'
                              : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200'
                            }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Mode switcher: Same time for all days vs custom time per day */}
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => canEdit && setIsDifferentTimePerDay(!isDifferentTimePerDay)}
                  className={`w-full flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-200 text-left transition-all ${!canEdit ? 'cursor-default opacity-85' : 'hover:bg-slate-100/80 cursor-pointer'
                    }`}
                >
                  <div className="space-y-0.5">
                    <span className="text-xs font-extrabold text-slate-900 block">Waktu Berbeza Mengikut Hari Latihan</span>
                    <span className="text-[11px] text-slate-500 block">
                      Aktifkan jika waktu hari Jumaat berbeza dengan hari Sabtu atau Ahad.
                    </span>
                  </div>
                  <div className="relative inline-flex items-center shrink-0">
                    <div className={`w-11 h-6 rounded-full transition-all relative ${isDifferentTimePerDay ? 'bg-asmm-red' : 'bg-slate-300'}`}>
                      <div className={`absolute top-[2px] w-5 h-5 bg-white rounded-full transition-all ${isDifferentTimePerDay ? 'left-[22px]' : 'left-[2px]'}`} />
                    </div>
                  </div>
                </button>

                {/* Standard Start Time & End Time (When same for all days) */}
                {!isDifferentTimePerDay ? (
                  <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-200 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                        <Timer className="w-4 h-4 text-asmm-red" />
                        <span>Masa Sesi Latihan Utama</span>
                      </span>
                      <span className="text-[10px] font-black text-slate-600 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                        Durasi: {calculateDuration(globalStartTime, globalEndTime)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                          <span>Masa Mula (Start Time) *</span>
                          <span className="text-[11px] font-mono font-black text-asmm-red">{time24To12(globalStartTime)}</span>
                        </label>
                        <div className="flex items-center bg-white border border-slate-300 rounded-xl px-3 py-2">
                          <Clock className="w-4 h-4 text-slate-400 mr-2 shrink-0" />
                          <input
                            type="time"
                            value={globalStartTime}
                            disabled={!canEdit}
                            onChange={(e) => setGlobalStartTime(e.target.value)}
                            className="w-full bg-transparent border-none focus:outline-none text-xs font-extrabold text-slate-900 disabled:opacity-60"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                          <span>Masa Tamat (End Time) *</span>
                          <span className="text-[11px] font-mono font-black text-emerald-700">{time24To12(globalEndTime)}</span>
                        </label>
                        <div className="flex items-center bg-white border border-slate-300 rounded-xl px-3 py-2">
                          <Clock className="w-4 h-4 text-slate-400 mr-2 shrink-0" />
                          <input
                            type="time"
                            value={globalEndTime}
                            disabled={!canEdit}
                            onChange={(e) => setGlobalEndTime(e.target.value)}
                            className="w-full bg-transparent border-none focus:outline-none text-xs font-extrabold text-slate-900 disabled:opacity-60"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Custom Start Time & End Time for each selected day */
                  <div className="space-y-3 bg-slate-50/80 p-4 rounded-2xl border border-slate-200">
                    <span className="text-xs font-black text-slate-900 block">
                      Tetapan Masa Mula & Tamat Mengikut Hari
                    </span>
                    {selectedDays.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Sila pilih sekurang-kurangnya satu hari latihan di atas.</p>
                    ) : (
                      selectedDays.map(dayKey => {
                        const dayObj = DAYS_OF_WEEK.find(d => d.key === dayKey);
                        const cur = dayTimes[dayKey] || { start: globalStartTime, end: globalEndTime };
                        const dur = calculateDuration(cur.start, cur.end);

                        return (
                          <div key={dayKey} className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs space-y-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-asmm-red" />
                                <span>Hari {dayObj?.label}</span>
                              </span>
                              <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                                {dur} ({time24To12(cur.start)} - {time24To12(cur.end)})
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 mb-1">Masa Mula (Start Time)</label>
                                <input
                                  type="time"
                                  value={cur.start}
                                  disabled={!canEdit}
                                  onChange={(e) => {
                                    setDayTimes(prev => ({
                                      ...prev,
                                      [dayKey]: { ...cur, start: e.target.value }
                                    }));
                                  }}
                                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 disabled:opacity-60"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 mb-1">Masa Tamat (End Time)</label>
                                <input
                                  type="time"
                                  value={cur.end}
                                  disabled={!canEdit}
                                  onChange={(e) => {
                                    setDayTimes(prev => ({
                                      ...prev,
                                      [dayKey]: { ...cur, end: e.target.value }
                                    }));
                                  }}
                                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 disabled:opacity-60"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Live Preview of Formatted Schedule */}
                <div className="p-4 bg-amber-50/70 border border-amber-200/80 rounded-2xl space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                      <span>Format Jadual Latihan Rasmi Dijana</span>
                    </span>
                    <span className="text-[10px] font-bold text-slate-500">Auto-sync dengan kalendar</span>
                  </div>
                  <div className="text-sm font-black text-slate-900">
                    {generatedScheduleString || '(Sila pilih hari latihan)'}
                  </div>
                  <p className="text-[11px] text-slate-600">
                    Format ini dipaparkan di portal awam, kalendar latihan gelanggang, dan invois bayaran bulanan pesilat.
                  </p>
                </div>

                {/* Smart Notice if activities have distinct times (e.g. Memanah vs Silat) */}
                {classesWithCustomTimes.length > 0 && (
                  <div className="p-3.5 bg-amber-50/80 rounded-2xl border border-amber-300 space-y-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-extrabold text-amber-950 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>Waktu Berbeza Mengikut Aktiviti Dikesan ({classesWithCustomTimes.length} Aktiviti)</span>
                      </span>
                      <span className="text-[10px] font-black text-amber-800 bg-amber-200/70 px-2 py-0.5 rounded-md">
                        Auto-Format
                      </span>
                    </div>
                    <p className="text-[11px] text-amber-900/90 leading-relaxed">
                      Aktiviti seperti <strong>Memanah</strong> dan <strong>Silat</strong> mempunyai waktu atau hari yang berbeza. Anda boleh tetapkan waktu setiap aktiviti di Bahagian 2 di bawah, atau tekan butang di bawah untuk menyelaraskan teks rasmi gelanggang.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <button
                        type="button"
                        disabled={!canEdit}
                        onClick={() => {
                          const combined = classesList.map(c => {
                            const sTime = c.startTime || globalStartTime;
                            const eTime = c.endTime || globalEndTime;
                            const timeRange = `${time24To12(sTime)} - ${time24To12(eTime)}`;
                            const dayStr = c.day ? `${c.day} ` : '';
                            return `${c.name}: ${dayStr}(${timeRange})`;
                          }).join(' & ');
                          setIsManualScheduleMode(true);
                          setManualSessionTime(combined);
                          showToast('info', 'Jadual Gabungan Digunakan', 'Jadual sesi gelanggang kini menyenaraikan waktu khusus setiap aktiviti.');
                        }}
                        className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-amber-400 font-extrabold rounded-xl text-xs cursor-pointer border-none shadow-2xs transition-all flex items-center gap-1.5"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                        <span>Jana Jadual Rasmi Gabungan Semua Aktiviti</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-xs font-bold text-slate-700">Teks Sesi Latihan Tersuai</label>
                <input
                  type="text"
                  value={manualSessionTime}
                  onChange={(e) => setManualSessionTime(e.target.value)}
                  placeholder="cth: Jumaat (8.30 PM - 10.30 PM) & Ahad (8.00 AM - 10.00 AM)"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                />
                <p className="text-[11px] text-slate-500">
                  Gunakan ini jika gelanggang mempunyai jadual khas luar kebiasaan yang tidak dapat diwakili oleh pemilih masa piawai.
                </p>
              </div>
            )}
          </div>

          {/* Card 2: Senarai Aktiviti, Kelas & Penetapan Yuran (With Start/End Time for each Class) */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2.5 rounded-2xl bg-red-50 text-asmm-red border border-red-100">
                  <HandFist className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-sm font-black text-slate-900">Aktiviti, Kelas & Penetapan Yuran</h2>
                  <p className="text-[11px] text-slate-500">Setiap kelas boleh mempunyai masa mula & tamat tersendiri serta kadar yuran.</p>
                </div>
              </div>
              <span className="text-xs font-black text-asmm-red bg-red-50 px-2.5 py-1 rounded-xl border border-red-100 self-start sm:self-center">
                {classesList.length} Kelas Ditawarkan
              </span>
            </div>

            {/* List of current classes with individual Start/End Time and Day configuration */}
            <div className="space-y-4">
              {classesList.map((cls, idx) => {
                const isMemanah = cls.name.toLowerCase().includes('memanah');
                const yearlyPrice = getYearlyPrice(cls.price);
                const classStart = cls.startTime || globalStartTime;
                const classEnd = cls.endTime || globalEndTime;
                const duration = calculateDuration(classStart, classEnd);
                const classDaysList = parseDaysFromDayString(cls.day);
                const hasCustomDays = classDaysList.length > 0 || (!!cls.day && cls.day !== 'Semua Hari Sesi' && cls.day !== 'Ikut Sesi Gelanggang');
                const hasCustomTime = (cls.startTime && cls.startTime !== globalStartTime) || (cls.endTime && cls.endTime !== globalEndTime);
                const isDistinctActivity = hasCustomDays || hasCustomTime;

                const enrolledInClass = enrolledMembers.filter(m => {
                  if (cls.name.toLowerCase().includes('memanah')) {
                    return m.sessionTime?.toLowerCase().includes('memanah');
                  }
                  if (cls.name.toLowerCase().includes('dewasa')) {
                    return m.relationship !== 'Anak' && (m.icNo ? isUserAbove18(m.icNo) : true);
                  }
                  if (cls.name.toLowerCase().includes('muda')) {
                    return m.relationship === 'Anak' || (m.icNo ? !isUserAbove18(m.icNo) : false);
                  }
                  return true;
                }).length;

                const currentFormattedSchedule = cls.day && cls.day !== 'Semua Hari Sesi' && cls.day !== 'Ikut Sesi Gelanggang'
                  ? `${cls.day} (${time24To12(classStart)} - ${time24To12(classEnd)})`
                  : `Ikut Hari Gelanggang (${time24To12(classStart)} - ${time24To12(classEnd)})`;

                return (
                  <div key={idx} className="p-4 sm:p-5 bg-slate-50/80 hover:bg-slate-50 rounded-2xl border border-slate-200 shadow-2xs transition-all space-y-4">
                    {/* Top Row: Class Name, Badges, Price, Delete */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200/80">
                      <div className="flex items-center gap-3">
                        <span className={`p-2.5 rounded-xl shrink-0 ${isMemanah ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-asmm-red'}`}>
                          {isMemanah ? <Target className="w-5 h-5" /> : <HandFist className="w-5 h-5" />}
                        </span>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-black text-slate-900">{cls.name}</span>
                            <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-white border border-slate-200 text-slate-700">
                              {cls.category || 'Terbuka'}
                            </span>
                            {isDistinctActivity && (
                              <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-amber-600" />
                                <span>Waktu Khusus Aktiviti</span>
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            {enrolledInClass} pesilat berdaftar &bull; Tempoh Sesi: <strong>{duration}</strong>
                          </span>
                        </div>
                      </div>

                      {/* Pricing inputs & delete */}
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="flex items-center bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 shadow-2xs">
                          <span className="text-xs font-bold text-slate-400 mr-1">RM</span>
                          <input
                            type="number"
                            min="0"
                            value={cls.price}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const val = Math.max(0, parseInt(e.target.value, 10) || 0);
                              updateClassItem(idx, { price: val });
                            }}
                            className="w-14 border-none focus:outline-none text-xs font-black text-slate-900 p-0 disabled:opacity-75"
                          />
                          <span className="text-[10px] font-bold text-slate-400">/bln</span>
                        </div>

                        <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-200 shrink-0">
                          RM{yearlyPrice}/thn (-10%)
                        </span>

                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleRemoveClass(idx)}
                            className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors border-none cursor-pointer"
                            title="Padam Kelas"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Schedule & Time Configuration Section */}
                    <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-slate-200 space-y-3.5">
                      {/* Banner: Current Live Schedule for this activity */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200/80">
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <Clock className="w-4 h-4 text-asmm-red shrink-0" />
                          <span className="text-slate-600 font-semibold">Jadual Sesi Aktiviti:</span>
                          <span className="font-mono font-black text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-200">
                            {currentFormattedSchedule}
                          </span>
                        </div>
                        <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 w-fit">
                          Durasi: {duration}
                        </span>
                      </div>

                      {/* 1. Day Configuration for this Class */}
                      <div className="space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                          <label className="text-[11px] font-extrabold text-slate-700 flex items-center gap-1.5">
                            <span>Hari Latihan Aktiviti Ini</span>
                            <span className="text-[10px] font-normal text-slate-500">
                              (cth: Memanah pada hari Ahad, Silat pada Jumaat & Sabtu)
                            </span>
                          </label>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              disabled={!canEdit}
                              onClick={() => updateClassItem(idx, { day: undefined })}
                              className={`text-[10px] font-black px-2 py-1 rounded border transition-all cursor-pointer ${
                                !hasCustomDays
                                  ? 'bg-slate-900 text-amber-400 border-slate-900'
                                  : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                              }`}
                            >
                              Ikut Hari Gelanggang
                            </button>
                            <button
                              type="button"
                              disabled={!canEdit}
                              onClick={() => {
                                if (!hasCustomDays) {
                                  updateClassItem(idx, { day: isMemanah ? 'Ahad' : 'Sabtu' });
                                }
                              }}
                              className={`text-[10px] font-black px-2 py-1 rounded border transition-all cursor-pointer ${
                                hasCustomDays
                                  ? 'bg-asmm-red text-white border-asmm-red'
                                  : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                              }`}
                            >
                              Hari Khusus
                            </button>
                          </div>
                        </div>

                        {/* Interactive Day Pills if Hari Khusus is active */}
                        {hasCustomDays && (
                          <div className="space-y-2 pt-1">
                            <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
                              {DAYS_OF_WEEK.map(d => {
                                const isDaySelected = classDaysList.includes(d.key);
                                return (
                                  <button
                                    key={d.key}
                                    type="button"
                                    disabled={!canEdit}
                                    onClick={() => {
                                      let newKeys: string[];
                                      if (isDaySelected) {
                                        newKeys = classDaysList.filter(k => k !== d.key);
                                      } else {
                                        newKeys = [...classDaysList, d.key];
                                      }
                                      const formatted = formatDaysFromKeys(newKeys);
                                      updateClassItem(idx, { day: formatted || undefined });
                                    }}
                                    className={`py-1.5 px-1 rounded-lg text-xs font-black transition-all border text-center cursor-pointer ${
                                      isDaySelected
                                        ? 'bg-asmm-red text-white border-asmm-red shadow-2xs'
                                        : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200'
                                    }`}
                                  >
                                    {d.label}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Quick Day Presets */}
                            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                              <span className="text-[10px] font-bold text-slate-400">Pilihan Pantas:</span>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { day: 'Ahad' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer"
                              >
                                Setiap Ahad {isMemanah ? '(Disyorkan Memanah)' : ''}
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { day: 'Sabtu' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer"
                              >
                                Setiap Sabtu
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { day: 'Sabtu & Ahad' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer"
                              >
                                Hujung Minggu (Sabtu & Ahad)
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { day: 'Jumaat & Sabtu' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer"
                              >
                                Jumaat & Sabtu
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 2. Start Time & End Time for this Class */}
                      <div className="space-y-2 pt-2 border-t border-slate-100">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1 flex items-center justify-between">
                              <span>Masa Mula (Start Time)</span>
                              <span className="font-mono font-bold text-asmm-red text-[11px]">{time24To12(classStart)}</span>
                            </label>
                            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 focus-within:border-asmm-red focus-within:bg-white">
                              <Clock className="w-3.5 h-3.5 text-slate-400 mr-1.5 shrink-0" />
                              <input
                                type="time"
                                value={classStart}
                                disabled={!canEdit}
                                onChange={(e) => updateClassItem(idx, { startTime: e.target.value })}
                                className="w-full bg-transparent border-none focus:outline-none text-xs font-bold text-slate-800 disabled:opacity-60"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-slate-600 mb-1 flex items-center justify-between">
                              <span>Masa Tamat (End Time)</span>
                              <span className="font-mono font-bold text-emerald-700 text-[11px]">{time24To12(classEnd)}</span>
                            </label>
                            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 focus-within:border-emerald-500 focus-within:bg-white">
                              <Clock className="w-3.5 h-3.5 text-slate-400 mr-1.5 shrink-0" />
                              <input
                                type="time"
                                value={classEnd}
                                disabled={!canEdit}
                                onChange={(e) => updateClassItem(idx, { endTime: e.target.value })}
                                className="w-full bg-transparent border-none focus:outline-none text-xs font-bold text-slate-800 disabled:opacity-60"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Quick Time Presets for this Class */}
                        <div className="flex items-center gap-1.5 flex-wrap pt-1">
                          <span className="text-[10px] font-bold text-slate-400">Waktu Cadangan:</span>
                          {isMemanah ? (
                            <>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { startTime: '08:00', endTime: '10:00' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded border border-amber-200 cursor-pointer"
                              >
                                Pagi (8.00 AM - 10.00 AM)
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { startTime: '08:30', endTime: '10:30' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded border border-amber-200 cursor-pointer"
                              >
                                Pagi (8.30 AM - 10.30 AM)
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { startTime: '17:00', endTime: '19:00' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded border border-amber-200 cursor-pointer"
                              >
                                Petang (5.00 PM - 7.00 PM)
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { startTime: '20:30', endTime: '22:30' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-red-50 hover:bg-red-100 text-asmm-red rounded border border-red-200 cursor-pointer"
                              >
                                Malam (8.30 PM - 10.30 PM)
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { startTime: '21:00', endTime: '23:00' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-red-50 hover:bg-red-100 text-asmm-red rounded border border-red-200 cursor-pointer"
                              >
                                Malam (9.00 PM - 11.00 PM)
                              </button>
                              <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateClassItem(idx, { startTime: '08:30', endTime: '10:30' })}
                                className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer"
                              >
                                Pagi (8.30 AM - 10.30 AM)
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={() => updateClassItem(idx, { startTime: globalStartTime, endTime: globalEndTime })}
                            className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded border border-slate-200 cursor-pointer"
                            title="Reset kepada waktu asas gelanggang"
                          >
                            Ikut Waktu Gelanggang ({time24To12(globalStartTime)} - {time24To12(globalEndTime)})
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Form: Tambah Kelas Baharu (with explicit Start Time, End Time & Day presets) */}
            {canEdit && (
              <div className="bg-slate-100/90 p-4 sm:p-5 rounded-2xl border border-slate-200 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-black text-slate-900 block flex items-center gap-1.5">
                    <Plus className="w-4 h-4 text-asmm-red" />
                    <span>Borang Tambah Kelas / Sesi Aktiviti Baharu</span>
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">
                    Boleh tetapkan waktu mula/tamat tersendiri
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Nama Aktiviti *</label>
                    <select
                      value={newClassName}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewClassName(val);
                        if (val === 'Silat Dewasa') {
                          setNewClassPrice(50);
                          setNewClassCategory('Dewasa');
                          setNewClassDay('Semua Hari Sesi');
                          setNewClassStartTime('20:30');
                          setNewClassEndTime('22:30');
                        } else if (val === 'Silat Muda') {
                          setNewClassPrice(30);
                          setNewClassCategory('Kanak-kanak');
                          setNewClassDay('Sabtu');
                          setNewClassStartTime('08:30');
                          setNewClassEndTime('10:30');
                        } else if (val === 'Silat') {
                          setNewClassPrice(40);
                          setNewClassCategory('Terbuka');
                          setNewClassDay('Semua Hari Sesi');
                          setNewClassStartTime('20:30');
                          setNewClassEndTime('22:30');
                        } else if (val === 'Memanah') {
                          setNewClassPrice(50);
                          setNewClassCategory('Terbuka');
                          setNewClassDay('Ahad');
                          setNewClassStartTime('08:00');
                          setNewClassEndTime('10:00');
                        } else if (val === 'Seni Tari & Senjata') {
                          setNewClassPrice(45);
                          setNewClassCategory('Terbuka');
                          setNewClassDay('Ahad');
                          setNewClassStartTime('10:30');
                          setNewClassEndTime('12:30');
                        }
                      }}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                    >
                      <option value="Silat">Silat</option>
                      <option value="Silat Dewasa">Silat Dewasa</option>
                      <option value="Silat Muda">Silat Muda</option>
                      <option value="Memanah">Memanah</option>
                      <option value="Seni Tari & Senjata">Seni Tari & Senjata</option>
                      <option value="Lain-lain">Lain-lain (Tulis Sendiri)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Yuran Bulanan (RM) *</label>
                    <div className="flex items-center bg-white border border-slate-300 rounded-xl px-3 py-1.5">
                      <span className="text-xs font-extrabold text-slate-400 mr-1.5">RM</span>
                      <input
                        type="number"
                        min="0"
                        value={newClassPrice}
                        onChange={(e) => setNewClassPrice(Math.max(0, parseInt(e.target.value, 10) || 0))}
                        className="w-full border-none focus:outline-none text-xs font-extrabold text-slate-900 p-0"
                      />
                      <span className="text-[10px] text-slate-400 font-bold">/bln</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Kategori Sasaran</label>
                    <select
                      value={newClassCategory}
                      onChange={(e) => setNewClassCategory(e.target.value as any)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                    >
                      <option value="Terbuka">Terbuka</option>
                      <option value="Dewasa">Dewasa (18+)</option>
                      <option value="Kanak-kanak">Kanak-kanak / Remaja (&lt;18)</option>
                    </select>
                  </div>

                  {newClassName === 'Lain-lain' && (
                    <div className="sm:col-span-3">
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Nama Kelas Kustom *</label>
                      <input
                        type="text"
                        value={customClassName}
                        onChange={(e) => setCustomClassName(e.target.value)}
                        placeholder="Masukkan nama aktiviti (contoh: Silat Tempur)"
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Hari Sesi Kelas</label>
                    <select
                      value={newClassDay}
                      onChange={(e) => setNewClassDay(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                    >
                      <option value="Semua Hari Sesi">Ikut Hari Sesi Gelanggang</option>
                      <option value="Ahad">Setiap Ahad (Disyorkan Memanah)</option>
                      <option value="Sabtu">Setiap Sabtu</option>
                      <option value="Sabtu & Ahad">Sabtu & Ahad</option>
                      <option value="Jumaat & Sabtu">Jumaat & Sabtu</option>
                      {DAYS_OF_WEEK.map(d => (
                        <option key={d.key} value={d.label}>Setiap {d.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1 flex items-center justify-between">
                      <span>Masa Mula (Start Time) *</span>
                      <span className="font-mono font-bold text-asmm-red text-[10px]">{time24To12(newClassStartTime)}</span>
                    </label>
                    <input
                      type="time"
                      value={newClassStartTime}
                      onChange={(e) => setNewClassStartTime(e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1 flex items-center justify-between">
                      <span>Masa Tamat (End Time) *</span>
                      <span className="font-mono font-bold text-emerald-700 text-[10px]">{time24To12(newClassEndTime)}</span>
                    </label>
                    <input
                      type="time"
                      value={newClassEndTime}
                      onChange={(e) => setNewClassEndTime(e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                    />
                  </div>
                </div>

                {/* Quick Time Preset Chips for New Class */}
                <div className="flex items-center gap-1.5 flex-wrap pt-0.5 text-xs">
                  <span className="text-[10px] font-bold text-slate-400">Pilihan Masa Pantas:</span>
                  <button
                    type="button"
                    onClick={() => {
                      setNewClassStartTime('08:00');
                      setNewClassEndTime('10:00');
                      setNewClassDay('Ahad');
                    }}
                    className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded border border-amber-300 cursor-pointer"
                  >
                    Memanah Pagi (Ahad 8.00 AM - 10.00 AM)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewClassStartTime('20:30');
                      setNewClassEndTime('22:30');
                    }}
                    className="text-[10px] font-bold px-2 py-0.5 bg-red-50 hover:bg-red-100 text-asmm-red rounded border border-red-200 cursor-pointer"
                  >
                    Silat Malam (8.30 PM - 10.30 PM)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewClassStartTime('08:30');
                      setNewClassEndTime('10:30');
                      setNewClassDay('Sabtu');
                    }}
                    className="text-[10px] font-bold px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded border border-slate-300 cursor-pointer"
                  >
                    Silat Muda Pagi (Sabtu 8.30 AM - 10.30 AM)
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleAddClass}
                  className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border-none shadow-xs"
                >
                  <Plus className="w-4 h-4 text-amber-400" />
                  <span>Tambahkan Kelas Ini ke Senarai</span>
                </button>
              </div>
            )}
          </div>

          {/* Card 3: Saluran Pembayaran Yuran Gelanggang (FPX Persatuan vs QR Sendiri Ketua Gelanggang) */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100">
                  <Wallet className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-sm font-black text-slate-900">Saluran Pembayaran Yuran Gelanggang</h2>
                  <p className="text-[11px] text-slate-500">Tetapkan saluran kutipan sama ada terus ke akaun rasmi persatuan (FPX) atau Kod QR sendiri.</p>
                </div>
              </div>
              <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${paymentPreference === 'qr_ketua'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}>
                {paymentPreference === 'qr_ketua' ? 'QR Ketua Gelanggang' : 'FPX Bank Persatuan'}
              </span>
            </div>

            {/* Pilihan Saluran Pembayaran: 2 Kad Pilihan */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Option 1: FPX ToyyibPay Bank Persatuan */}
              <div
                onClick={() => canEdit && setPaymentPreference('fpx')}
                className={`p-4 rounded-2xl border-2 transition-all flex flex-col justify-between space-y-3 ${canEdit ? 'cursor-pointer' : 'cursor-default opacity-90'
                  } ${paymentPreference === 'fpx'
                    ? 'bg-emerald-50/40 border-emerald-500 shadow-xs ring-2 ring-emerald-500/10'
                    : 'bg-slate-50/60 hover:bg-slate-50 border-slate-200 text-slate-700'
                  }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2 rounded-xl ${paymentPreference === 'fpx' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-xs font-black text-slate-900 block">FPX Bank Persatuan</span>
                      <span className="text-[10px] text-slate-500 block">ToyyibPay Online Gateway</span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${paymentPreference === 'fpx' ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white'}`}>
                    {paymentPreference === 'fpx' && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </div>

                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Semua yuran disalurkan terus secara automatik ke akaun rasmi <strong>Bank Muamalat Persatuan (0203-0003921-71-0)</strong> melalui FPX.
                </p>

                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                  <span>Audit & e-Laporan</span>
                  <span className="font-bold text-emerald-700">Automatik</span>
                </div>
              </div>

              {/* Option 2: Kod QR Sendiri Ketua Gelanggang */}
              <div
                onClick={() => canEdit && setPaymentPreference('qr_ketua')}
                className={`p-4 rounded-2xl border-2 transition-all flex flex-col justify-between space-y-3 ${canEdit ? 'cursor-pointer' : 'cursor-default opacity-90'
                  } ${paymentPreference === 'qr_ketua'
                    ? 'bg-amber-50/50 border-amber-500 shadow-xs ring-2 ring-amber-500/10'
                    : 'bg-slate-50/60 hover:bg-slate-50 border-slate-200 text-slate-700'
                  }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2 rounded-xl ${paymentPreference === 'qr_ketua' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                      <QrCode className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-xs font-black text-slate-900 block">QR Sendiri Ketua Gelanggang</span>
                      <span className="text-[10px] text-slate-500 block">DuitNow QR / Akaun Bank</span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${paymentPreference === 'qr_ketua' ? 'border-amber-600 bg-amber-600' : 'border-slate-300 bg-white'}`}>
                    {paymentPreference === 'qr_ketua' && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                </div>

                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Yuran dibayar terus ke Kod QR DuitNow atau akaun bank Ketua Gelanggang. Pembayar <strong>wajib memuat naik resit bayaran (.pdf)</strong>.
                </p>

                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                  <span>Format Resit Simpanan</span>
                  <span className="font-bold text-amber-700 font-mono text-[9px]">memberid_name_month_year.pdf</span>
                </div>
              </div>
            </div>

            {/* Konfigurasi Butiran Bank & Kod QR (Jika QR Sendiri dipilih) */}
            {paymentPreference === 'qr_ketua' && (
              <div className="space-y-4 p-4.5 bg-gradient-to-b from-amber-50/30 to-slate-50/80 rounded-2xl border border-amber-200/80 animate-fade-in">
                <div className="flex items-center gap-2 text-amber-800 text-xs font-black">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span>Konfigurasi Kod QR & Akaun Bank Ketua Gelanggang</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Nama Bank */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nama Bank *</label>
                    <select
                      value={qrBankName}
                      disabled={!canEdit}
                      onChange={(e) => setQrBankName(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    >
                      <option value="Maybank">Maybank (Malayan Banking)</option>
                      <option value="CIMB Bank">CIMB Bank</option>
                      <option value="Bank Islam">Bank Islam Malaysia</option>
                      <option value="RHB Bank">RHB Bank</option>
                      <option value="BSN (Bank Simpanan Nasional)">BSN (Bank Simpanan Nasional)</option>
                      <option value="Public Bank">Public Bank</option>
                      <option value="Hong Leong Bank">Hong Leong Bank</option>
                      <option value="AmBank">AmBank</option>
                      <option value="Bank Muamalat">Bank Muamalat Malaysia Berhad</option>
                      <option value="Bank Rakyat">Bank Rakyat</option>
                      <option value="Agrobank">Agrobank</option>
                      <option value="DuitNow QR Sahaja">DuitNow QR Sahaja</option>
                    </select>
                  </div>

                  {/* Nama Pemegang Akaun */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nama Pemegang Akaun *</label>
                    <input
                      type="text"
                      value={qrAccountName}
                      disabled={!canEdit}
                      onChange={(e) => setQrAccountName(e.target.value)}
                      placeholder="cth: Mohd Fauzi Bin Abd Aziz"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    />
                  </div>

                  {/* Nombor Akaun Bank */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nombor Akaun Bank *</label>
                    <input
                      type="text"
                      value={qrAccountNo}
                      disabled={!canEdit}
                      onChange={(e) => setQrAccountNo(e.target.value.replace(/[^0-9-]/g, ''))}
                      placeholder="cth: 152012345678"
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold font-mono text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    />
                  </div>

                  {/* Muat Naik Fail Kod QR */}
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">
                      <span>Imej Kod QR DuitNow (PNG / JPEG)</span>
                      {isQrUploading && <span className="text-amber-600 ml-1.5 font-normal">Memproses...</span>}
                    </label>
                    {canEdit ? (
                      <label className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 hover:border-amber-500 rounded-xl text-xs font-bold text-slate-700 cursor-pointer transition-colors">
                        <Upload className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="truncate">{qrImageUrl ? 'Ganti Fail Gambar QR' : 'Pilih Gambar QR DuitNow'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleQrImageUpload}
                          className="hidden"
                        />
                      </label>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-500">
                        <Upload className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">{qrImageUrl ? 'Gambar QR Telah Dimuat Naik' : 'Tiada Gambar QR'}</span>
                      </div>
                    )}
                  </div>

                  {/* Nota / Arahan Tambahan */}
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-700 mb-1">Nota / Arahan Tambahan untuk Pembayar Yuran</label>
                    <input
                      type="text"
                      value={qrNotes}
                      disabled={!canEdit}
                      onChange={(e) => setQrNotes(e.target.value)}
                      placeholder="cth: Sila jelaskan yuran sebelum 7hb setiap bulan dan lampirkan resit transaksi."
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:border-amber-500 disabled:opacity-75"
                    />
                  </div>
                </div>

                {/* Pratonton Kod QR & Maklumat Bank */}
                <div className="mt-3 p-3.5 bg-white rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center gap-4">
                  {qrImageUrl ? (
                    <div className="relative shrink-0 group">
                      <img
                        src={qrImageUrl}
                        alt="Kod QR Ketua Gelanggang"
                        className="w-28 h-28 object-contain rounded-xl border border-slate-200 shadow-3xs bg-white p-1"
                      />
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setQrImageUrl('')}
                          className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-xs border-none cursor-pointer"
                          title="Buang Imej QR"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="w-28 h-28 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400 p-2 text-center shrink-0">
                      <QrCode className="w-8 h-8 mb-1 text-slate-300" />
                      <span className="text-[10px] font-bold leading-tight">Tiada Gambar QR Dimuat Naik</span>
                    </div>
                  )}

                  <div className="space-y-1.5 min-w-0 flex-1 text-center sm:text-left">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                      <span className="px-2 py-0.5 bg-slate-900 text-amber-400 text-[10px] font-mono font-black rounded-md">
                        {qrBankName || 'Bank'}
                      </span>
                      {qrAccountNo && (
                        <span className="text-xs font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                          {qrAccountNo}
                        </span>
                      )}
                    </div>
                    <div className="text-xs font-extrabold text-slate-900 truncate">
                      {qrAccountName || dynamicLeader.name || 'Nama Pemegang Akaun'}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-tight">
                      {qrNotes || 'Pesilat akan melihat butiran akaun ini semasa membuat bayaran yuran dan dikehendaki memuat naik resit.'}
                    </p>
                    <div className="text-[10px] text-indigo-700 font-bold flex items-center justify-center sm:justify-start gap-1">
                      <FileText className="w-3 h-3" />
                      <span>Resit disimpan ke Google Drive JSON Ahli (memberid_name_month_year.pdf)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right 1 Column: Management Details & Financial Summary */}
        <div className="space-y-6">

          {/* Card 3: Maklumat & Butiran Pengurusan Gelanggang */}
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-2xs space-y-4">
            <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <span className="p-2.5 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-sm font-black text-slate-900">Butiran Gelanggang & Lokasi</h3>
                <p className="text-[11px] text-slate-500">Ketua gelanggang, tempat dan pandu arah.</p>
              </div>
            </div>

            {/* Status Aktif / Tidak Aktif */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Status Gelanggang</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setStatus('Aktif')}
                  className={`py-2 px-3 rounded-xl text-xs font-extrabold border transition-all cursor-pointer ${status === 'Aktif'
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-xs'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Aktif
                </button>
                <button
                  type="button"
                  onClick={() => setStatus('Tidak Aktif')}
                  className={`py-2 px-3 rounded-xl text-xs font-extrabold border transition-all cursor-pointer ${status === 'Tidak Aktif'
                      ? 'bg-slate-700 text-white border-slate-700 shadow-xs'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                >
                  Tidak Aktif
                </button>
              </div>
            </div>

            {/* Dynamic Ketua Gelanggang & No. Telefon Display (Non-editable, dynamically assigned from member roles) */}
            <div className="bg-slate-50/90 p-3.5 rounded-2xl border border-slate-200/80 space-y-3">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-slate-400" />
                <span>Ketua Gelanggang</span>
              </span>

              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center text-xs font-black shrink-0 shadow-2xs">
                    {dynamicLeader.name ? dynamicLeader.name.trim().charAt(0).toUpperCase() : 'K'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-black text-slate-900 block truncate" title={dynamicLeader.name}>
                      {dynamicLeader.name ? formatPublicName(dynamicLeader.name) : 'Belum Dilantik'}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono block leading-none mt-0.5">
                      {dynamicLeader.memberId ? `${dynamicLeader.memberId} • ` : ''}Ketua Gelanggang
                    </span>
                  </div>
                </div>

                {dynamicLeader.bengkung && (
                  <BengkungCulaBadge
                    bengkung={dynamicLeader.bengkung}
                    size="sm"
                    fullWidth={false}
                    className="!h-6 !text-[10px] !pl-2.5 !pr-7 rounded-lg shrink-0 shadow-3xs"
                    minWidth="min-w-[85px]"
                  />
                )}
              </div>

              {/* No. Telefon / WhatsApp */}
              <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-700 font-bold min-w-0 flex-1">
                  <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="truncate font-mono text-[11px]">
                    {dynamicLeader.phone || 'Tiada No. Telefon'}
                  </span>
                </div>

                {dynamicLeader.phone && (
                  <a
                    href={`https://wa.me/${dynamicLeader.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-colors shrink-0 shadow-3xs"
                    title="Hubungi WhatsApp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                )}
              </div>
            </div>

            {/* Nama Tempat / Lokasi */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Nama Pusat / Tempat Latihan</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="cth: Pusat Riadah Wadi AnNour"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red focus:bg-white"
              />
            </div>

            {/* Alamat Penuh */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Lengkap</label>
              <textarea
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Alamat fizikal gelanggang untuk surat & penghantaran"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:border-asmm-red focus:bg-white resize-none"
              />
            </div>

            {/* Pautan GPS / Google Maps */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Pautan GPS / Google Maps</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={gpsLocation}
                  onChange={(e) => setGpsLocation(e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:border-asmm-red focus:bg-white"
                />
                {(gpsLocation || address || location) && (
                  <a
                    href={getGoogleMapsUrl({ gpsLocation, address, location })}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors border border-blue-200 shrink-0"
                    title="Uji Pautan Peta"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Card 4: Ringkasan Pesilat & Unjuran Kutipan */}
          <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-2xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="p-2 rounded-xl bg-slate-900 text-amber-400 shrink-0 shadow-2xs">
                  <Users className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-xs font-black text-slate-900 tracking-tight truncate">Ringkasan Pesilat & Yuran</h3>
                  <p className="text-[10px] text-slate-400 truncate">Kutipan yuran bulanan & senarai ahli</p>
                </div>
              </div>
              <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/80 shrink-0 whitespace-nowrap">
                Aktif
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <div className="bg-slate-50/90 hover:bg-slate-100/60 transition-colors p-3 rounded-2xl border border-slate-200/80 flex flex-col justify-between">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">
                  Pesilat Berdaftar
                </span>
                <div className="my-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-slate-900 leading-none">
                    {enrolledMembers.length}
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">orang</span>
                </div>
                <span className="text-[9px] text-slate-400 font-medium block">
                  ahli gelanggang aktif
                </span>
              </div>

              <div className="bg-emerald-50/70 hover:bg-emerald-50 transition-colors p-3 rounded-2xl border border-emerald-200/80 flex flex-col justify-between">
                <span className="text-[9px] font-black text-emerald-800 uppercase tracking-wider block">
                  Unjuran Bulanan
                </span>
                <div className="my-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-emerald-700 leading-none">
                    RM{financialSummary.monthlyTotal}
                  </span>
                  <span className="text-[10px] font-bold text-emerald-600">/ bln</span>
                </div>
                <span className="text-[9px] text-emerald-700/80 font-medium block">
                  anggaran kutipan
                </span>
              </div>
            </div>

            <div className="p-3 bg-gradient-to-r from-emerald-50/80 via-teal-50/40 to-slate-50 border border-emerald-200/70 rounded-2xl flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-3xs">
                  <DollarSign className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-black text-slate-900">Pakej Tahunan</span>
                    <span className="text-[8px] font-black text-emerald-700 bg-emerald-100/90 px-1.5 py-0.5 rounded-sm uppercase tracking-wide leading-none">
                      -10% Jimat
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 block truncate">
                    Jika dibayar penuh 12 bulan
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-black text-emerald-800 leading-none">
                  RM{financialSummary.yearlyTotal}
                </div>
                <div className="text-[9px] font-bold text-slate-400 leading-none mt-0.5">
                  / tahun
                </div>
              </div>
            </div>

            {/* List of enrolled members */}
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                  Senarai Pesilat Gelanggang ({enrolledMembers.length})
                </span>
                <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Aktif Latihan
                </span>
              </div>
              {enrolledMembers.length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1 -mr-1">
                  {enrolledMembers.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-2 rounded-xl bg-slate-50/80 hover:bg-slate-100/90 border border-slate-200/70 transition-all text-xs gap-2 group"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-7 h-7 rounded-lg bg-white border border-slate-200/90 text-slate-800 flex items-center justify-center text-xs font-black shrink-0 shadow-3xs group-hover:border-slate-300">
                          {m.name?.trim()?.charAt(0)?.toUpperCase() || 'P'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-extrabold text-slate-900 block truncate text-[11px]" title={m.name}>
                            {formatPublicName(m.name)}
                          </span>
                          <span className="text-[9px] text-slate-400 font-mono block leading-none mt-0.5">
                            {m.id}
                          </span>
                        </div>
                      </div>
                      <BengkungCulaBadge
                        bengkung={m.bengkung || 'Putih 1'}
                        size="sm"
                        fullWidth={false}
                        className="!h-6 !text-[10px] !pl-2 !pr-7 rounded-lg shrink-0 shadow-3xs"
                        minWidth="min-w-[85px]"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-xs text-slate-400 italic bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  Tiada pesilat aktif berdaftar di gelanggang ini.
                </div>
              )}
            </div>
          </div>

          {/* Bottom Save Action */}
          {canEdit && (
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
              className="w-full py-3 bg-asmm-red hover:bg-asmm-crimson disabled:opacity-50 text-white text-xs font-black rounded-2xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer border-none"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Menyimpan Perubahan...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Simpan Semua Perubahan Sesi & Harga</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
